import { LegalLayout } from '../components/LegalLayout';
import { Shield } from 'lucide-react';

export default function AITransparencyPage() {
  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      content: (
        <>
          <p>
            This AI Transparency Statement is published to meet transparency obligations under the <strong>EU Artificial Intelligence Act (Articles 50–54)</strong> and to align with <strong>UK GDPR principles</strong> on fairness and openness.
          </p>
          <p>
            Visainterviews.ai, operated by Cloud Lab Technologies Ltd, provides AI-powered voice assistant solutions that allow businesses to automate inbound and outbound calls, online chats, and messaging interactions through natural, human-like conversations.
          </p>
          <p>
            This document explains how our AI systems work, how data is processed, what limitations exist, and how we protect users' rights.
          </p>
        </>
      )
    },
    {
      id: "provider-identity",
      title: "Provider Identity",
      content: (
        <>
          <ul>
            <li><strong>Organisation:</strong> Cloud Lab Technologies Ltd (trading as Visainterviews.ai)</li>
            <li><strong>Registered Address:</strong> 4 Crown Place, Liverpool Street, London, EC2A 4BT</li>
            <li><strong>Website:</strong> <a href="https://visainterviews.ai" className="text-lime-600 dark:text-lime-400 hover:underline">https://visainterviews.ai</a></li>
            <li><strong>Contact (Privacy):</strong> <a href="mailto:privacy@visainterviews.ai" className="text-lime-600 dark:text-lime-400 hover:underline">privacy@visainterviews.ai</a></li>
          </ul>
        </>
      )
    },
    {
      id: "nature-purpose",
      title: "Nature and Purpose of the AI System",
      content: (
        <>
          <p>Visainterviews.ai offers synthetic voice-based and text-based assistants that:</p>
          <ul>
            <li>Automate inbound and outbound calls</li>
            <li>Manage appointment bookings and reminders</li>
            <li>Handle lead qualification and customer queries</li>
            <li>Assist with product/service information</li>
            <li>Collect customer data for CRM or operational workflows</li>
          </ul>
          <p>
            The system operates 24/7, supports multiple languages and accents, and can be customised for industry-specific use cases. The primary purpose is to replace repetitive manual communication tasks, improve service efficiency, and reduce wait times for customers.
          </p>
        </>
      )
    },
    {
      id: "functionality",
      title: "AI System Functionality",
      content: (
        <>
          <p>Our AI system includes the following capabilities:</p>
          <ul>
            <li>Orchestration layer combining in-house automation logic with third-party AI models (e.g., OpenAI, ElevenLabs, Google Speech)</li>
            <li>Real-time speech recognition (ASR) and natural language understanding (NLU)</li>
            <li>Realistic text-to-speech output using licensed AI voice models</li>
            <li>Optional voice cloning (only with explicit, written consent)</li>
            <li>Integrations with APIs such as Twilio, Meta WhatsApp Cloud API, Google Calendar, CRM platforms</li>
            <li>Built-in escalation logic to hand over to a human agent where required</li>
            <li>Conversation monitoring for quality control and service improvement</li>
          </ul>
        </>
      )
    },
    {
      id: "human-disclosure",
      title: "Human Interaction Disclosure",
      content: (
        <>
          <p>
            When interacting with Visainterviews.ai's AI assistants, users may hear a synthetic voice. Our system <strong>always discloses its non-human nature</strong> at the start of the interaction, for example:
          </p>
          <blockquote className="border-l-4 border-lime-500 pl-4 italic bg-lime-50 dark:bg-lime-950/30 py-3 pr-3 rounded-r-lg">
            "Hello, this is an AI assistant from Visainterviews.ai calling on behalf of [client name]. I'm not a human, but I'm here to help you."
          </blockquote>
          <p>
            This ensures transparency under EU AI Act Article 50 and similar UK fairness principles.
          </p>
        </>
      )
    },
    {
      id: "data-categories",
      title: "Data Categories and Processing",
      content: (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left font-semibold">Data Category</th>
                  <th className="py-3 px-4 text-left font-semibold">Examples</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="py-3 px-4">Audio & Voice Data</td>
                  <td className="py-3 px-4">Call recordings, speech-to-text transcripts</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 px-4">Identifiers</td>
                  <td className="py-3 px-4">Phone numbers, caller IDs, session IDs</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 px-4">Usage Data</td>
                  <td className="py-3 px-4">Interaction logs, timestamps, success/error rates</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 px-4">CRM / Optional Data</td>
                  <td className="py-3 px-4">Names, booking details, customer notes, email addresses</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4">
            <strong>Retention:</strong> Default up to 90 days (unless contractual terms specify otherwise). Deletion or anonymisation is applied after the retention period expires.
          </p>
          <p>
            <strong>Legal Basis:</strong> Performance of contract (Art. 6(1)(b) UK GDPR) and legitimate interests (Art. 6(1)(f) UK GDPR – service optimisation, fraud prevention).
          </p>
        </>
      )
    },
    {
      id: "subprocessors",
      title: "Subprocessors & Hosting",
      content: (
        <>
          <p>Visainterviews.ai uses vetted subprocessors to deliver services. Our key current providers include:</p>
          <ul>
            <li><strong>Amazon Web Services (AWS)</strong> – UK/EU hosting infrastructure</li>
            <li><strong>Twilio</strong> – Telephony infrastructure</li>
            <li><strong>OpenAI</strong> – Natural language processing</li>
            <li><strong>ElevenLabs</strong> – Text-to-speech voice synthesis</li>
            <li><strong>Google Cloud</strong> – Speech recognition and analytics</li>
            <li><strong>Meta (WhatsApp Cloud API)</strong> – Messaging integration</li>
          </ul>
          <p>
            Data is hosted in the UK/EU where possible; where data leaves the UK/EU, appropriate safeguards (e.g., IDTA, SCCs) are applied.
          </p>
        </>
      )
    },
    {
      id: "limitations",
      title: "Model Limitations & Risks",
      content: (
        <>
          <p><strong>Known limitations:</strong></p>
          <ul>
            <li>AI responses may sometimes be incomplete, factually inaccurate, or contextually irrelevant ("hallucinations")</li>
            <li>AI cannot provide regulated professional advice (legal, financial, medical)</li>
            <li>Strong accents, background noise, or unusual speech patterns may reduce accuracy</li>
            <li>Sentiment detection and intent classification may occasionally misinterpret tone or urgency</li>
          </ul>
          <p><strong>Mitigations:</strong></p>
          <ul>
            <li>Human fallback routing for sensitive or complex queries</li>
            <li>Regular testing against diverse inputs</li>
            <li>Continuous model performance monitoring and updates</li>
          </ul>
        </>
      )
    },
    {
      id: "consent",
      title: "Consent and Legal Compliance for Outbound AI Calls",
      content: (
        <>
          <p>
            Outbound AI calls are only made where the client confirms valid consent from the recipient (per UK GDPR, PECR, and applicable telecom laws). Call recipients are informed immediately that they are speaking to an AI assistant. Opt-out requests are recorded and honoured.
          </p>
          <p><strong>Example outbound disclosure:</strong></p>
          <blockquote className="border-l-4 border-lime-500 pl-4 italic bg-lime-50 dark:bg-lime-950/30 py-3 pr-3 rounded-r-lg">
            "This is an AI-powered call from Visainterviews.ai on behalf of [client]. If you prefer to speak to a human, please say so or press [key]."
          </blockquote>
        </>
      )
    },
    {
      id: "rights",
      title: "Rights of Individuals",
      content: (
        <>
          <p>Individuals interacting with Visainterviews.ai systems may exercise UK GDPR rights, including:</p>
          <ul>
            <li>Access to personal data</li>
            <li>Rectification of inaccuracies</li>
            <li>Erasure of data (subject to retention policies)</li>
            <li>Restriction or objection to processing</li>
            <li>Withdrawal of consent</li>
            <li>Complaint to the Information Commissioner's Office (ICO)</li>
          </ul>
          <p>
            <strong>Contact for rights requests:</strong>{' '}
            <a href="mailto:privacy@visainterviews.ai" className="text-lime-600 dark:text-lime-400 hover:underline">
              privacy@visainterviews.ai
            </a>
          </p>
        </>
      )
    },
    {
      id: "updates",
      title: "Updates and Version Control",
      content: (
        <>
          <p>
            This AI Transparency Statement will be reviewed at least annually and updated as required. The latest version is always available at:{' '}
            <a href="/ai-transparency" className="text-lime-600 dark:text-lime-400 hover:underline">
              https://visainterviews.ai/ai-transparency
            </a>
          </p>
        </>
      )
    }
  ];

  return (
    <LegalLayout 
      title="AI Transparency Statement"
      effectiveDate="1 August 2025"
      lastUpdated="Version 1.0"
      sections={sections}
      icon={Shield}
    />
  );
}
